import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { 
  Handshake, 
  Search,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Building2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

import { API_BASE_URL } from '../utils/api';
const API_URL = API_BASE_URL;

export default function AdminPartners() {
  const { admin } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin-portal/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data.partners);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const updatePartnerStatus = async (partnerId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin-portal/partners/${partnerId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Partner ${status}`);
      fetchPartners();
    } catch (error) {
      toast.error('Failed to update partner status');
    }
  };

  const filteredPartners = partners.filter(p => 
    p.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Partners</h1>
          <p className="text-slate-400 mt-1">
            Manage partner accounts and wholesale pricing
          </p>
        </div>
        <Button variant="outline" onClick={fetchPartners} className="border-slate-600 text-slate-300 hover:bg-slate-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search partners..."
            className="pl-10 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Partners List */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">All Partners ({total})</CardTitle>
          <CardDescription className="text-slate-400">
            Partners receive wholesale pricing for referred customers
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredPartners.length > 0 ? (
            <div className="space-y-4">
              {filteredPartners.map(partner => (
                <div key={partner.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-700/30">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                      <Handshake className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{partner.company_name}</p>
                      <p className="text-sm text-slate-400">{partner.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">
                          <Building2 className="w-3 h-3 mr-1" />
                          {partner.customer_count} customers
                        </Badge>
                        <Badge variant="outline" className="text-xs font-mono border-slate-600 text-slate-300">
                          {partner.referral_code}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right mr-4">
                      <p className="text-xs text-slate-400">Registered</p>
                      <p className="text-sm text-slate-300">{formatDate(partner.created_at)}</p>
                    </div>
                    <Badge className={
                      partner.status === 'active' ? 'bg-green-500' :
                      partner.status === 'pending' ? 'bg-amber-500' :
                      partner.status === 'suspended' ? 'bg-red-500' : 'bg-slate-500'
                    }>
                      {partner.status}
                    </Badge>
                    <div className="flex gap-2 ml-2">
                      {partner.status === 'pending' && (
                        <>
                          <Button 
                            size="sm" 
                            onClick={() => updatePartnerStatus(partner.id, 'active')}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => updatePartnerStatus(partner.id, 'rejected')}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {partner.status === 'active' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updatePartnerStatus(partner.id, 'suspended')}
                          className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                          Suspend
                        </Button>
                      )}
                      {partner.status === 'suspended' && (
                        <Button 
                          size="sm" 
                          onClick={() => updatePartnerStatus(partner.id, 'active')}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Handshake className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No partners yet</h3>
              <p className="text-slate-400">Partners can register at /partner/login</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
